/**
 * src/services/pastBroadcastsService.ts
 *
 * REST client for saved live broadcasts ("Past broadcasts" on a
 * profile).  Mirrors `server/routes/broadcasts.js` 1:1.
 *
 * Two distinct auth flows:
 *   - Upload uses the same HMAC `X-Live-Session-Grant` the live tools
 *     surface uses, paired with the `X-Live-Session-Id`.  This binds
 *     the recording to a still-active paid session and prevents
 *     anonymous replays from being injected onto someone's profile.
 *   - Pin / unpin / delete use a SEP-10–style Stellar signature
 *     challenge: the server issues a non-submittable XDR transaction,
 *     the wallet signs it, and the server verifies the signature
 *     against the wallet's public key.  The challenge is single-use
 *     and expires in 5 minutes — see `requestOwnershipChallenge` and
 *     `proveOwnership` below.
 */

import type { SigningWallet } from './profileService';

const DEFAULT_BACKEND = (
  (typeof process !== 'undefined' && (process as any).env?.REACT_APP_BACKEND_URL) || ''
);

export interface SavedBroadcast {
  id: string;
  /** Payer wallet — has pin/delete authority. */
  wallet: string;
  /**
   * Room owner identity (slice 4f-c). For human broadcasts this is
   * the same as `wallet`. For AI agent broadcasts it's the agent's
   * G-address, so the agent's profile can list its own VOD.
   * Defaults to `wallet` for legacy rows persisted before slice 4f-c.
   */
  broadcasterId: string;
  /**
   * Parent/creator account that launched the agent (agent broadcasts
   * only; null for human broadcasts). Lets the agent's VOD link back
   * to "who runs me", and a person's profile surface their agents'
   * broadcasts. The same wallet holds pin/delete authority.
   */
  creatorWallet?: string | null;
  sessionId: string | null;
  title: string | null;
  startedAt: string;
  endedAt: string;
  durationSec: number;
  fileBytes: number;
  mimeType: string;
  pinned: boolean;
  viewCount: number;
  giftTotal: number;
  expiresAt: string;
  createdAt: string;
  hasEvents: boolean;
}

/**
 * Captured timeline of tool / gift events that fired while the
 * recording was being made.  Replayed over the video player so the
 * VOD looks identical to the original live.
 */
export interface RecordedEvent {
  /** Milliseconds since the recording started.  Drives playback sync. */
  relativeMs: number;
  kind: 'tool' | 'gift';
  /** Original WS payload for the event — opaque to this service.
   *  The replay layer re-dispatches it through the existing event
   *  buses so the existing layers handle rendering. */
  payload: any;
}

function backendUrl(override?: string): string {
  return override || DEFAULT_BACKEND
    || (typeof window !== 'undefined' ? window.location.origin : '');
}

async function _json<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    let reason = fallback;
    try {
      const j = await res.json();
      if (j && (j.error || j.reason)) reason = j.error || j.reason;
    } catch { /* ignore */ }
    throw new Error(reason);
  }
  return res.json() as Promise<T>;
}

// ── List + read ─────────────────────────────────────────────────────

export async function listBroadcasts(
  wallet: string,
  opts: { backend?: string } = {},
): Promise<SavedBroadcast[]> {
  const url = `${backendUrl(opts.backend)}/api/broadcasts/list?wallet=${encodeURIComponent(wallet)}`;
  const res = await fetch(url);
  const data = await _json<{ ok: boolean; recordings: SavedBroadcast[] }>(res, 'list_failed');
  return data.recordings || [];
}

export async function getBroadcast(
  id: string,
  opts: { backend?: string } = {},
): Promise<SavedBroadcast> {
  const res = await fetch(`${backendUrl(opts.backend)}/api/broadcasts/${encodeURIComponent(id)}`);
  const data = await _json<{ ok: boolean; recording: SavedBroadcast }>(res, 'get_failed');
  return data.recording;
}

export async function getBroadcastEvents(
  id: string,
  opts: { backend?: string } = {},
): Promise<RecordedEvent[]> {
  const res = await fetch(
    `${backendUrl(opts.backend)}/api/broadcasts/${encodeURIComponent(id)}/events`,
  );
  const data = await _json<{ ok: boolean; events: RecordedEvent[] }>(res, 'events_failed');
  return data.events || [];
}

/** Resolve the URL of the streaming video file (Range-supported). */
export function videoUrl(id: string, opts: { backend?: string } = {}): string {
  return `${backendUrl(opts.backend)}/api/broadcasts/${encodeURIComponent(id)}/video`;
}

// ── Upload ──────────────────────────────────────────────────────────

export interface UploadInput {
  sessionId: string;
  grantToken: string;
  blob: Blob;
  startedAt?: string;
  endedAt?: string;
  durationSec?: number;
  title?: string | null;
  viewCount?: number;
  giftTotal?: number;
  events?: RecordedEvent[];
}

/**
 * Tiny `await sleep(ms)` helper.  Defined at module scope rather
 * than inline inside the chunked-upload retry loop because ESLint's
 * `no-loop-func` rule (correctly) flags the closure form
 * `(r) => setTimeout(r, baseMs * attempts)` when `attempts` is a
 * `let` declared in the enclosing loop — even though we always
 * await it before the next iteration.  Hoisting also makes the
 * exponential-backoff schedule trivial to unit-test in isolation.
 */
function _sleep(ms: number): Promise<void> {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

/**
 * Heuristic ceiling above which we always start a chunked upload
 * up-front.  Below this we try a single-shot POST first (one round
 * trip, simpler) and fall back to chunked on common upload failures.
 *
 * The 12 MB threshold was chosen so a recording that fits comfortably
 * inside a default Cloudflare front-door body buffer goes through the
 * fast path — anything larger always uses chunks.
 */
const SINGLE_SHOT_BLOB_LIMIT = 12 * 1024 * 1024;

/**
 * Send a finished recording to the backend.  For small recordings
 * the entire blob is POSTed in one request; for larger recordings
 * (or when the single-shot fails on a proxy timeout / 413 / 502 /
 * network error) we transparently fall back to a chunked upload
 * that streams the blob in bounded pieces.  Either way, callers
 * just `await uploadBroadcast(...)` and get back the final
 * `SavedBroadcast` metadata.
 */
export async function uploadBroadcast(
  input: UploadInput,
  opts: { backend?: string; onProgress?: (sentBytes: number, totalBytes: number) => void } = {},
): Promise<SavedBroadcast> {
  const totalBytes = input.blob.size;
  if (totalBytes <= SINGLE_SHOT_BLOB_LIMIT) {
    try {
      const result = await _uploadSingleShot(input, opts);
      opts.onProgress?.(totalBytes, totalBytes);
      return result;
    } catch (err: any) {
      // Common failure modes that mean "the blob made it to the
      // proxy but never landed in the worker": fall back to chunked
      // so a temporary front-door issue doesn't lose the recording.
      const fallbackable = _shouldFallbackToChunked(err);
      if (!fallbackable) throw err;
    }
  }
  return _uploadChunked(input, opts);
}

function _shouldFallbackToChunked(err: any): boolean {
  if (!err) return false;
  const msg = String(err.message || err).toLowerCase();
  return msg.includes('502') || msg.includes('504')
      || msg.includes('413')
      || msg.includes('upload_failed')
      || msg.includes('failed to fetch')
      || msg.includes('networkerror')
      || msg.includes('aborted')
      || msg.includes('empty_body');
}

async function _uploadSingleShot(
  input: UploadInput,
  opts: { backend?: string },
): Promise<SavedBroadcast> {
  const params = new URLSearchParams();
  if (input.startedAt)   params.set('startedAt',   input.startedAt);
  if (input.endedAt)     params.set('endedAt',     input.endedAt);
  if (input.durationSec) params.set('durationSec', String(input.durationSec));
  if (input.title)       params.set('title',       input.title);
  if (input.viewCount)   params.set('viewCount',   String(input.viewCount));
  if (input.giftTotal)   params.set('giftTotal',   String(input.giftTotal));

  const headers: Record<string, string> = {
    'Content-Type':         input.blob.type || 'video/webm',
    'X-Live-Session-Id':    input.sessionId,
    'X-Live-Session-Grant': input.grantToken,
  };
  if (input.events && input.events.length > 0) {
    const trimmed = input.events.slice(0, 5000);
    const json    = JSON.stringify(trimmed);
    headers['X-Broadcast-Events'] = btoa(unescape(encodeURIComponent(json)));
  }

  const res = await fetch(
    `${backendUrl(opts.backend)}/api/broadcasts/recordings?${params.toString()}`,
    { method: 'POST', headers, body: input.blob },
  );
  const data = await _json<{ ok: boolean; recording: SavedBroadcast }>(res, 'upload_failed');
  return data.recording;
}

/**
 * Chunked-upload path.  Three phases:
 *   1. POST /init               → server returns uploadId + maxPartBytes
 *   2. POST /part/<i>           → one per slice, sequential, with retry
 *   3. POST /finalize           → server reassembles + returns SavedBroadcast
 *
 * Each part is small enough to land inside any reasonable proxy
 * window, so a 30-minute recording on a slow connection works the
 * same way a 30-second one does on fast Wi-Fi.
 */
async function _uploadChunked(
  input: UploadInput,
  opts: { backend?: string; onProgress?: (sentBytes: number, totalBytes: number) => void },
): Promise<SavedBroadcast> {
  const url = backendUrl(opts.backend);
  const ownerHeaders: Record<string, string> = {
    'X-Live-Session-Id':    input.sessionId,
    'X-Live-Session-Grant': input.grantToken,
  };

  // 1. Init
  const initRes = await fetch(`${url}/api/broadcasts/recordings/chunked/init`, {
    method: 'POST',
    headers: { ...ownerHeaders, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      mimeType:    input.blob.type || 'video/webm',
      title:       input.title || null,
      startedAt:   input.startedAt || null,
      endedAt:     input.endedAt || null,
      durationSec: input.durationSec || 0,
      viewCount:   input.viewCount || 0,
      giftTotal:   input.giftTotal || 0,
    }),
  });
  const init = await _json<{ ok: boolean; uploadId: string; maxPartBytes: number }>(initRes, 'init_failed');

  // 2. Parts
  const totalBytes = input.blob.size;
  const partSize   = Math.max(64 * 1024, Math.min(init.maxPartBytes, 4 * 1024 * 1024));
  const totalParts = Math.max(1, Math.ceil(totalBytes / partSize));
  let sent = 0;
  for (let i = 0; i < totalParts; i++) {
    const start = i * partSize;
    const end   = Math.min(start + partSize, totalBytes);
    const slice = input.blob.slice(start, end, input.blob.type || 'video/webm');

    let attempts = 0;
    while (true) {
      attempts += 1;
      try {
        const partRes = await fetch(
          `${url}/api/broadcasts/recordings/chunked/${encodeURIComponent(init.uploadId)}/part/${i}`,
          {
            method: 'POST',
            headers: { ...ownerHeaders, 'Content-Type': slice.type || 'video/webm' },
            body: slice,
          },
        );
        await _json<{ ok: boolean }>(partRes, 'part_failed');
        sent += slice.size;
        opts.onProgress?.(sent, totalBytes);
        break;
      } catch (err: any) {
        // Fail fast on bad-request / quota / payload-too-large —
        // those are deterministic and won't get better on retry.
        const msg = String(err?.message || '').toLowerCase();
        const fatal = msg.includes('part_too_large')
                   || msg.includes('video_too_large')
                   || msg.includes('quota_exceeded')
                   || msg.includes('upload_not_found')
                   || msg.includes('upload_wallet_mismatch')
                   || msg.includes('invalid_part_index');
        if (fatal || attempts >= 3) throw err;
        // Exponential backoff: 500ms, 1s, 2s — bounded so we don't
        // hold the user's "Saving recording…" toast hostage on a
        // dead network.
        await _sleep(500 * attempts);
      }
    }
  }

  // 3. Finalize
  const finalizeRes = await fetch(
    `${url}/api/broadcasts/recordings/chunked/${encodeURIComponent(init.uploadId)}/finalize`,
    {
      method: 'POST',
      headers: { ...ownerHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        expectedParts: totalParts,
        events: input.events ? input.events.slice(0, 5000) : [],
      }),
    },
  );
  const data = await _json<{ ok: boolean; recording: SavedBroadcast }>(finalizeRes, 'finalize_failed');
  return data.recording;
}

// ── Stellar-signed ownership proofs ──────────────────────────────────

export interface OwnershipChallenge {
  challengeId: string;
  /** base64-encoded TransactionEnvelope the wallet must sign. */
  transactionXdr: string;
  /** Network passphrase the wallet must sign for. */
  networkPassphrase: string;
  expiresAt: string;
}

/**
 * Ask the server for a fresh challenge for `wallet`.  The returned
 * XDR is non-submittable (sequence=0, source=wallet, manageData op
 * containing a server-issued nonce).  Sign it with the wallet's
 * keypair, then call the action with the signed envelope.
 */
export async function requestOwnershipChallenge(
  wallet: string,
  opts: { backend?: string; network?: string } = {},
): Promise<OwnershipChallenge> {
  const params = new URLSearchParams({ wallet });
  if (opts.network) params.set('network', opts.network);
  const res = await fetch(
    `${backendUrl(opts.backend)}/api/broadcasts/auth/challenge?${params.toString()}`,
  );
  const data = await _json<{ ok: boolean } & OwnershipChallenge>(res, 'challenge_failed');
  return data;
}

/**
 * Sign a freshly issued challenge with the connected wallet.
 *
 * `SigningWallet.signTransaction` is the same primitive Freighter,
 * the wallets-kit, and the passkey signer all expose, so this works
 * uniformly across every connection method GeoTrust supports.
 */
export async function signOwnershipChallenge(
  challenge: OwnershipChallenge,
  signer: SigningWallet,
): Promise<string> {
  return signer.signTransaction(challenge.transactionXdr, challenge.networkPassphrase);
}

/**
 * Convenience wrapper that does the request → sign → return-headers
 * dance in one call.  Returns the headers ready to attach to a
 * pin/unpin/delete request.
 */
async function _proveOwnership(
  wallet: string,
  signer: SigningWallet,
  opts: { backend?: string; network?: string },
): Promise<Record<string, string>> {
  const challenge = await requestOwnershipChallenge(wallet, opts);
  const signedXdr = await signOwnershipChallenge(challenge, signer);
  return {
    'X-Owner-Wallet':   wallet,
    'X-Owner-Challenge': challenge.challengeId,
    'X-Owner-Signed-Tx': signedXdr,
  };
}

// ── Pin / unpin / delete (Stellar-signed) ───────────────────────────

export interface OwnerActionOpts {
  /** Backend base URL override (testing). */
  backend?: string;
  /** Network passphrase override; defaults to whatever the server's
   *  STELLAR_NETWORK is configured for. */
  network?: string;
}

export async function pinBroadcast(
  id: string,
  ownerWallet: string,
  signer: SigningWallet,
  opts: OwnerActionOpts = {},
): Promise<SavedBroadcast> {
  const headers = await _proveOwnership(ownerWallet, signer, opts);
  const res = await fetch(
    `${backendUrl(opts.backend)}/api/broadcasts/${encodeURIComponent(id)}/pin`,
    { method: 'POST', headers },
  );
  const data = await _json<{ ok: boolean; recording: SavedBroadcast }>(res, 'pin_failed');
  return data.recording;
}

export async function unpinBroadcast(
  id: string,
  ownerWallet: string,
  signer: SigningWallet,
  opts: OwnerActionOpts = {},
): Promise<SavedBroadcast> {
  const headers = await _proveOwnership(ownerWallet, signer, opts);
  const res = await fetch(
    `${backendUrl(opts.backend)}/api/broadcasts/${encodeURIComponent(id)}/pin`,
    { method: 'DELETE', headers },
  );
  const data = await _json<{ ok: boolean; recording: SavedBroadcast }>(res, 'unpin_failed');
  return data.recording;
}

export async function deleteBroadcast(
  id: string,
  ownerWallet: string,
  signer: SigningWallet,
  opts: OwnerActionOpts = {},
): Promise<void> {
  const headers = await _proveOwnership(ownerWallet, signer, opts);
  const res = await fetch(
    `${backendUrl(opts.backend)}/api/broadcasts/${encodeURIComponent(id)}`,
    { method: 'DELETE', headers },
  );
  await _json<{ ok: boolean }>(res, 'delete_failed');
}
